import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Typography, MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import AuthApi from '../../../api/AuthApi';
import PractitionerApi from '../../../api/PractitionerApi';
import LoadingScreen from './../loadingScreen';
import {
    Asterisk,
    Title,
    ContainerNumber,
    PINStaffContainer,
    LoginAvatar,
    Avatar,
    PinPadButtonContainer,
    PinPadButton,
    PinCardTitle
} from './styles';
import { useCookies } from 'react-cookie';
import { TOKEN } from '../../../../collums-constants/storageKeys';
import { validateJobPermissions, getCookieEnvironment } from '../../../helpers';
import Intercom from '../../../helpers/Intercom';
import { BroadcastChannel } from 'broadcast-channel';
import { enqueuePassiveLoginRedirect, enqueueLogoutRedirect } from '../../../api/config';
import { DEFAULT_PHOTO } from '../../../../collums-constants';

const bcOptions = { type: 'localstorage', webWorkerSupport: false };
const authBroadcastChannel = new BroadcastChannel('collums-lockscreen', bcOptions);

const theme = createMuiTheme({
    props: {
        MuiButtonBase: {
            disableRipple: false,
            disableTouchRipple: false
        }
    }
});

const ShowPINModal = ({
    // isOpen,
    // setOpen,
    onCorrectPin,
    style = {},
    onEnterPin = () => {}
}) => {
    const [allPractitioners, setAllPractitioners] = useState([]);
    const [loginAsPractitioner, setLoginAsPractitioner] = useState(null);
    const [pin, setPin] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [blockedSessions, setBlockedSessions] = useState(null);
    const [blockedPin, setBlockedPin] = useState(null);

    const [cookies, setCookie] = useCookies([TOKEN, 'isLocked']);
    const [isOpen, setOpen] = useState(cookies.isLocked);

    authBroadcastChannel.onmessage = async event => {
        const jsonData = JSON.parse(event);
        const { token, user, logout } = jsonData;

        if (logout) {
            localStorage.removeItem('token');
            enqueuePassiveLoginRedirect();
            return;
        }

        if (!token) {
            setOpen(true);
        } else {
            await handleNewToken({ token, user });
        }
    };

    const handleNewToken = async ({ token: newToken, user }) => {
        if (!newToken) {
            return;
        }
        const environment = getCookieEnvironment();

        localStorage.setItem('token', newToken);

        const res = await AuthApi.getMe();

        await validateJobPermissions(newToken, res, process.env.REACT_APP_NAME);
        await Intercom.updateUser(user ? user : null);
        Intercom.showLauncher();

        setIsLoading(true);
        setIsRefreshing(true);

        setCookie('token', newToken, environment);
        setCookie('isLocked', false, environment);

        onEnterPin(res, newToken);

        if (onCorrectPin) {
            await onCorrectPin();
            return;
        }

        window.location.reload();
    };

    useEffect(() => {
        if (isRefreshing) return;

        const isLocked = cookies.isLocked === 'true';
        setOpen(isLocked);
    }, [cookies, isRefreshing]);

    // Poll isLocked cookie for cross-tab/cross-origin changes
    useEffect(() => {
        if (isRefreshing) return;
        const interval = setInterval(() => {
            const match = document.cookie.match(/(?:^|;\s*)isLocked=([^;]*)/);
            const locked = match ? match[1] === 'true' : false;
            if (locked && !isOpen) {
                setOpen(true);
            } else if (!locked && isOpen) {
                window.location.reload();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [isOpen, isRefreshing]);

    // Poll isLoggedOut cookie for cross-tab/cross-origin logout
    useEffect(() => {
        const loggedOutRef = { current: document.cookie.match(/(?:^|;\s*)isLoggedOut=([^;]*)/) };
        const interval = setInterval(() => {
            const match = document.cookie.match(/(?:^|;\s*)isLoggedOut=([^;]*)/);
            if (match && match[1] !== loggedOutRef.current?.[1]) {
                localStorage.removeItem('token');
                enqueuePassiveLoginRedirect();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Poll token cookie for cross-tab/cross-origin re-login
    useEffect(() => {
        const interval = setInterval(() => {
            const localToken = localStorage.getItem('token');
            if (localToken) return; // already logged in
            const lockedMatch = document.cookie.match(/(?:^|;\s*)isLocked=([^;]*)/);
            if (lockedMatch && lockedMatch[1] === 'true') return; // device is locked, don't auto-login
            const match = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
            if (match && match[1]) {
                // Another tab logged in — pick up the token
                localStorage.setItem('token', match[1]);
                window.location.reload();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Validate token when tab becomes visible (e.g. after laptop wake)
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.hidden) return;
            if (isOpen) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            try {
                await AuthApi.getMe();
            } catch (e) {
                // Network error — ignore, will retry on next visibility change
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [isOpen]);

    useEffect(() => {
        (async () => {
            const allPractitioners = await PractitionerApi.listPinLoginPractitioners();
            setAllPractitioners(allPractitioners);
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (isOpen === 'true' || isOpen === true) {
            Intercom.hideLauncher();
        }
    }, [isOpen]);

    const pinSubmittingRef = React.useRef(false);

    const checkPin = useCallback(
        async _pin => {
            if (_pin?.length === 5 && !pinSubmittingRef.current) {
                try {
                    pinSubmittingRef.current = true;
                    setIsLoading(true);
                    // calls verify-pin endpoint
                    const isCorrectPIN = await AuthApi.isCorrectPIN({
                        pin: _pin?.join('').toString(),
                        practitionerId: loginAsPractitioner?.id
                    });

                    if (isCorrectPIN) {
                        const token = isCorrectPIN.token;
                        if (token) {
                            authBroadcastChannel.postMessage(JSON.stringify({ token: token, user: isCorrectPIN.user }));
                            await handleNewToken({ token: token, user: isCorrectPIN.user });
                            return; // Page will reload — don't clear loading
                        } else {
                            await Intercom.updateUser();
                            Intercom.showLauncher();
                        }
                    }
                } catch (e) {
                    if (e?.status === 403 && e?.data?.sessions) {
                        setBlockedSessions(e.data.sessions);
                        setBlockedPin(_pin?.join('').toString());
                        setPin([]);
                        return;
                    }
                } finally {
                    pinSubmittingRef.current = false;
                    setIsLoading(false);
                }
            }
        },
        // eslint-disable-next-line
        [loginAsPractitioner]
    );

    const practitionerAvatar = practitioner => {
        const defaultAvatar =
            practitioner.gender === 'Female' ? DEFAULT_PHOTO.DEFAULT_FEMALE : DEFAULT_PHOTO.DEFAULT_MALE;

        // const avatar = practitioner.avatar
        //     ? 'https://collums.imgix.net/' + practitioner.avatar.split('/').pop() + '?w=128&h=128&mask=ellipse&fit=crop'
        //     : null;

        return practitioner.avatar ?? defaultAvatar;
    };

    const handleDeleteBlockedSession = async sessionId => {
        try {
            await AuthApi.deleteSessionByPin({ pin: blockedPin, sessionId });
            setBlockedSessions(prev => prev.filter(s => s._id !== sessionId));
        } catch (e) {
            // ignore
        }
    };

    const returnFromBlockedSessions = () => {
        setBlockedSessions(null);
        setBlockedPin(null);
        setPin([]);
    };

    const returnToStaffSelect = () => {
        setLoginAsPractitioner(null);
        setPin([]);
    };

    const onClickNumber = value => {
        if (pin.length < 5) {
            setPin(nPin => [...nPin, value]);
        }
        checkPin([...pin, value]);
    };

    const onClickPractitioner = value => {
        setLoginAsPractitioner(value);
    };

    const removeLastNumber = () => {
        const nPin = [...pin];
        nPin.pop();
        setPin(nPin);
    };

    const handleNumberType = useCallback(
        ({ key }) => {
            if (!isOpen) return;
            if (key === 'Backspace') setPin(oldPin => oldPin.slice(0, -1));
            if (isNaN(key)) return;
            if (pin.length < 5) {
                setPin(oldPin => [...oldPin, Number(key)]);
            }
            checkPin([...pin, Number(key)]);
        },
        [checkPin, pin, isOpen]
    );

    useEffect(() => {
        window.addEventListener('keyup', handleNumberType);
        return () => {
            window.removeEventListener('keyup', handleNumberType);
        };
    }, [pin, handleNumberType]);

    const StyledPinPadButton = useCallback(content => {
        return (
            <PinPadButtonContainer
                item
                xs={4}
                sm={4}
                md={4}
                lg={4}
                justifyContent="center"
                key={`${content}-login-avatar`}
                style={{ display: 'flex' }}
            >
                <PinPadButton onClick={content.onClick}>{content.children}</PinPadButton>
            </PinPadButtonContainer>
        );
    }, []);

    return (
        <>
            <MuiThemeProvider theme={theme}>
                <PINStaffContainer style={{ position: 'absolute', display: isOpen ? 'block' : 'none', ...style }}>
                    {isLoading && <LoadingScreen />}
                    {blockedSessions && blockedSessions.length > 0 ? (
                        <Box
                            mx="auto"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="90%"
                            height="90%"
                            bgcolor="#ffffff"
                        >
                            <Title align="center" variant="h5" style={{ fontWeight: 500 }}>
                                Device limit reached
                            </Title>
                            <Typography style={{ fontSize: 14, color: '#d32f2f', marginTop: 8, textAlign: 'center' }}>
                                You are currently logged into {blockedSessions.length} devices. Please log out of one
                                before trying again.
                            </Typography>
                            <Box mt={2} width="100%" maxWidth="400px">
                                {blockedSessions.map(session => {
                                    const deviceName = (() => {
                                        const { deviceModel, deviceVendor, os } = session;
                                        if (deviceModel === 'iPhone' || deviceModel === 'iPad') return deviceModel;
                                        if (deviceModel && deviceVendor && !deviceModel.startsWith(deviceVendor)) {
                                            return `${deviceVendor} ${deviceModel}`;
                                        }
                                        if (deviceModel) return deviceModel;
                                        if (deviceVendor === 'Apple') return 'Mac';
                                        if (os) return os.split(' ')[0];
                                        return null;
                                    })();
                                    const browserOs = session.os || null;
                                    return (
                                        <Box
                                            key={session._id}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            py={0.5}
                                            style={{ borderBottom: '1px solid #e0e0e0' }}
                                        >
                                            <Box>
                                                {deviceName && (
                                                    <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                                                        {deviceName}
                                                        {session.deviceType && session.deviceType !== 'desktop'
                                                            ? ` (${session.deviceType})`
                                                            : ''}
                                                    </Typography>
                                                )}
                                                {browserOs && (
                                                    <Typography style={{ fontSize: 11, color: '#888' }}>
                                                        {browserOs}
                                                    </Typography>
                                                )}
                                                <Typography style={{ fontSize: 11, color: '#888' }}>
                                                    Last active: {new Date(session.lastUsedAt).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                style={{
                                                    fontSize: 12,
                                                    color: '#d32f2f',
                                                    cursor: 'pointer',
                                                    minWidth: 'auto',
                                                    whiteSpace: 'nowrap',
                                                    marginLeft: 8
                                                }}
                                                onClick={() => handleDeleteBlockedSession(session._id)}
                                            >
                                                Log out
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Typography
                                style={{
                                    fontSize: 13,
                                    color: '#1a73e8',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    marginTop: 16
                                }}
                                onClick={returnFromBlockedSessions}
                            >
                                Back
                            </Typography>
                        </Box>
                    ) : loginAsPractitioner ? (
                        <Box
                            container
                            mx="auto"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="90%"
                            height="90%"
                            bgcolor="#ffffff"
                        >
                            <LoginAvatar>
                                <Avatar alt="Staff avatar" src={practitionerAvatar(loginAsPractitioner)}></Avatar>
                                <PinCardTitle align="center" variant="h5" style={{ fontWeight: 450 }}>
                                    {loginAsPractitioner.displayName}
                                </PinCardTitle>
                            </LoginAvatar>
                            <Box display="flex" width="100%" mt={2} className="header">
                                <Title align="center" variant="h5" style={{ fontWeight: 500 }}>
                                    Please enter your PIN
                                    <br />
                                    or{' '}
                                    <span
                                        onClick={enqueueLogoutRedirect}
                                        style={{ textDecoration: 'underline', cursor: 'pointer', color: '#1a73e8' }}
                                    >
                                        log in
                                    </span>{' '}
                                    again
                                </Title>
                            </Box>
                            <Box width="1rem" height="1rem" />
                            <ContainerNumber display="flex" width="100%" justifyContent="center" height="14px">
                                {pin.map((v, idx) => {
                                    return (
                                        <Typography key={idx}>
                                            <Asterisk />
                                        </Typography>
                                    );
                                })}
                            </ContainerNumber>
                            <Box width="1rem" height="1rem" />
                            <Grid
                                container
                                spacing={3}
                                direction="row"
                                justifyContent="center"
                                style={{ maxWidth: '300px' }}
                            >
                                <StyledPinPadButton onClick={() => onClickNumber(1)}>1</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(2)}>2</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(3)}>3</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(4)}>4</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(5)}>5</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(6)}>6</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(7)}>7</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(8)}>8</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(9)}>9</StyledPinPadButton>
                                <StyledPinPadButton onClick={returnToStaffSelect}>&lt;</StyledPinPadButton>
                                <StyledPinPadButton onClick={() => onClickNumber(0)}>0</StyledPinPadButton>
                                <StyledPinPadButton onClick={removeLastNumber} noPadding>
                                    Del
                                </StyledPinPadButton>
                            </Grid>
                        </Box>
                    ) : (
                        <Box
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            mt={3}
                            mx="auto"
                            width="90%"
                            bgcolor="#ffffff"
                            maxWidth="800px"
                        >
                            <Box width="100%" mt={1} className="header">
                                <Title align="center" variant="h5" style={{ fontWeight: 500 }}>
                                    Device locked.
                                </Title>
                                <Title align="center" variant="h5" style={{ fontWeight: 500 }}>
                                    Select your practitioner and enter PIN or{' '}
                                    <span
                                        onClick={enqueueLogoutRedirect}
                                        style={{ textDecoration: 'underline', cursor: 'pointer', color: '#1a73e8' }}
                                    >
                                        log in
                                    </span>{' '}
                                    again.
                                </Title>
                            </Box>
                            <Box width="1rem" height="1rem" />
                            <Grid container spacing={1} direction="row" justifyContent="center">
                                {allPractitioners.map((p, index) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={3}
                                            justifyContent="center"
                                            key={`${index}-login-avatar`}
                                            style={{ display: 'flex' }}
                                        >
                                            <LoginAvatar onClick={() => onClickPractitioner(p)}>
                                                <Avatar alt="Staff avatar" src={practitionerAvatar(p)}></Avatar>
                                                <PinCardTitle
                                                    align="center"
                                                    variant="h5"
                                                    mt={3}
                                                    style={{ fontWeight: 450 }}
                                                >
                                                    {p.displayName}
                                                </PinCardTitle>
                                            </LoginAvatar>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}
                </PINStaffContainer>
            </MuiThemeProvider>
        </>
    );
};

ShowPINModal.propTypes = {
    style: PropTypes.object,
    onCorrectPin: PropTypes.func,
    onEnterPin: PropTypes.func
};

export default ShowPINModal;
